// BL-177 Step 2j: a CRM contact's TIME ZONE - suggested from the primary address first, then from the phone number.
//
// The contact stores an IANA name (America/New_York) in contacts.time_zone plus WHO set it in contacts.time_zone_source:
//   'auto'    suggested here from the address / phone (refreshed whenever the address or phone changes)
//   'manual'  chosen by a person on the contact card - auto-fill never overwrites it (the database enforces that too)
// The scheduler reads contact.time_zone (or resolveContactTimeZone(contact) when the column is still empty).
//
// Pure functions only (no network). The phone half is src/lib/scheduledSend.js recipientTimeZoneForPhone (US/Canada area codes
// and international country codes); this file adds the ADDRESS half and the glue.
//
// KNOWN LIMITS, on purpose:
//   - A US state or Canadian province that spans zones gets its main zone (table below): Florida/Texas/Kansas/Tennessee/Kentucky/
//     Idaho/Michigan/Indiana/Nebraska/the Dakotas/Oregon/Nevada, and Ontario/BC/Quebec edges. A person corrects the rare exception.
//   - Countries with several zones (Mexico, Brazil, Argentina, Chile, Russia, Australia, Indonesia, ...) are not guessed from the
//     address; the phone number is tried next.
//   - Only the PRIMARY address is read (a second address may be a holiday home); with no flagged primary the first one is.

import { recipientTimeZoneForPhone, zoneAbbreviation } from './scheduledSend';
import { phoneForSave } from './phoneSave';
import { entriesOf } from './contactMethods';

const ET = 'America/New_York';
const CT = 'America/Chicago';
const MT = 'America/Denver';
const PT = 'America/Los_Angeles';

/** Main zone of each US state / territory (a state split across zones gets the zone most of its people live in). */
export const US_STATE_ZONES = Object.freeze({
    AL: CT, AK: 'America/Anchorage', AZ: 'America/Phoenix', AR: CT, CA: PT, CO: MT, CT: ET, DE: ET, DC: ET,
    FL: ET, GA: ET, HI: 'Pacific/Honolulu', ID: MT, IL: CT, IN: ET, IA: CT, KS: CT, KY: ET, LA: CT, ME: ET,
    MD: ET, MA: ET, MI: ET, MN: CT, MS: CT, MO: CT, MT, NE: CT, NV: PT, NH: ET, NJ: ET, NM: MT, NY: ET, NC: ET,
    ND: CT, OH: ET, OK: CT, OR: PT, PA: ET, RI: ET, SC: ET, SD: CT, TN: CT, TX: CT, UT: MT, VT: ET, VA: ET,
    WA: PT, WV: ET, WI: CT, WY: MT,
    PR: 'America/Puerto_Rico', VI: 'America/St_Thomas', GU: 'Pacific/Guam', AS: 'Pacific/Pago_Pago', MP: 'Pacific/Saipan',
});

/** Main zone of each Canadian province / territory. */
export const CA_PROVINCE_ZONES = Object.freeze({
    ON: 'America/Toronto', QC: 'America/Toronto', NS: 'America/Halifax', NB: 'America/Halifax', PE: 'America/Halifax',
    NL: 'America/St_Johns', MB: 'America/Winnipeg', SK: 'America/Regina', AB: 'America/Edmonton', BC: 'America/Vancouver',
    YT: 'America/Whitehorse', NT: 'America/Yellowknife', NU: 'America/Iqaluit',
});

const US_STATE_NAMES = Object.freeze({
    ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR', CALIFORNIA: 'CA', COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE',
    'DISTRICT OF COLUMBIA': 'DC', FLORIDA: 'FL', GEORGIA: 'GA', HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA',
    KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME', MARYLAND: 'MD', MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN',
    MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE', NEVADA: 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', OHIO: 'OH', OKLAHOMA: 'OK', OREGON: 'OR',
    PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC', 'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT',
    VERMONT: 'VT', VIRGINIA: 'VA', WASHINGTON: 'WA', 'WEST VIRGINIA': 'WV', WISCONSIN: 'WI', WYOMING: 'WY',
    'PUERTO RICO': 'PR', 'US VIRGIN ISLANDS': 'VI', 'VIRGIN ISLANDS': 'VI', GUAM: 'GU', 'AMERICAN SAMOA': 'AS', 'NORTHERN MARIANA ISLANDS': 'MP',
});

const CA_PROVINCE_NAMES = Object.freeze({
    ONTARIO: 'ON', QUEBEC: 'QC', 'NOVA SCOTIA': 'NS', 'NEW BRUNSWICK': 'NB', 'PRINCE EDWARD ISLAND': 'PE', 'NEWFOUNDLAND AND LABRADOR': 'NL',
    NEWFOUNDLAND: 'NL', MANITOBA: 'MB', SASKATCHEWAN: 'SK', ALBERTA: 'AB', 'BRITISH COLUMBIA': 'BC', YUKON: 'YT',
    'NORTHWEST TERRITORIES': 'NT', NUNAVUT: 'NU',
});

/** Countries that live in one time zone (or whose capital's zone is the right call), by ISO 3166-1 alpha-2. */
export const COUNTRY_ZONES = Object.freeze({
    GB: 'Europe/London', IE: 'Europe/Dublin', DE: 'Europe/Berlin', FR: 'Europe/Paris', ES: 'Europe/Madrid', IT: 'Europe/Rome',
    NL: 'Europe/Amsterdam', BE: 'Europe/Brussels', LU: 'Europe/Luxembourg', CH: 'Europe/Zurich', AT: 'Europe/Vienna', PT: 'Europe/Lisbon',
    SE: 'Europe/Stockholm', NO: 'Europe/Oslo', DK: 'Europe/Copenhagen', FI: 'Europe/Helsinki', IS: 'Atlantic/Reykjavik', PL: 'Europe/Warsaw',
    GR: 'Europe/Athens', CZ: 'Europe/Prague', SK: 'Europe/Bratislava', HU: 'Europe/Budapest', RO: 'Europe/Bucharest', BG: 'Europe/Sofia',
    HR: 'Europe/Zagreb', RS: 'Europe/Belgrade', UA: 'Europe/Kyiv', TR: 'Europe/Istanbul', CY: 'Asia/Nicosia', MT: 'Europe/Malta',
    CO: 'America/Bogota', PE: 'America/Lima', VE: 'America/Caracas', EC: 'America/Guayaquil', UY: 'America/Montevideo', CR: 'America/Costa_Rica',
    PA: 'America/Panama', JM: 'America/Jamaica', DO: 'America/Santo_Domingo', BS: 'America/Nassau', BB: 'America/Barbados', TT: 'America/Port_of_Spain',
    IN: 'Asia/Kolkata', PK: 'Asia/Karachi', BD: 'Asia/Dhaka', LK: 'Asia/Colombo', NP: 'Asia/Kathmandu', JP: 'Asia/Tokyo', KR: 'Asia/Seoul',
    CN: 'Asia/Shanghai', HK: 'Asia/Hong_Kong', TW: 'Asia/Taipei', SG: 'Asia/Singapore', MY: 'Asia/Kuala_Lumpur', PH: 'Asia/Manila',
    TH: 'Asia/Bangkok', VN: 'Asia/Ho_Chi_Minh', AE: 'Asia/Dubai', SA: 'Asia/Riyadh', QA: 'Asia/Qatar', KW: 'Asia/Kuwait', IL: 'Asia/Jerusalem',
    JO: 'Asia/Amman', LB: 'Asia/Beirut', EG: 'Africa/Cairo', NG: 'Africa/Lagos', GH: 'Africa/Accra', KE: 'Africa/Nairobi', ZA: 'Africa/Johannesburg',
    MA: 'Africa/Casablanca', NZ: 'Pacific/Auckland',
});

const COUNTRY_NAMES = Object.freeze({
    'UNITED KINGDOM': 'GB', UK: 'GB', 'GREAT BRITAIN': 'GB', ENGLAND: 'GB', SCOTLAND: 'GB', WALES: 'GB', 'NORTHERN IRELAND': 'GB', GB: 'GB',
    IRELAND: 'IE', GERMANY: 'DE', DEUTSCHLAND: 'DE', FRANCE: 'FR', SPAIN: 'ES', ESPANA: 'ES', ITALY: 'IT', ITALIA: 'IT', NETHERLANDS: 'NL',
    'THE NETHERLANDS': 'NL', HOLLAND: 'NL', BELGIUM: 'BE', LUXEMBOURG: 'LU', SWITZERLAND: 'CH', AUSTRIA: 'AT', PORTUGAL: 'PT', SWEDEN: 'SE',
    NORWAY: 'NO', DENMARK: 'DK', FINLAND: 'FI', ICELAND: 'IS', POLAND: 'PL', GREECE: 'GR', 'CZECH REPUBLIC': 'CZ', CZECHIA: 'CZ',
    SLOVAKIA: 'SK', HUNGARY: 'HU', ROMANIA: 'RO', BULGARIA: 'BG', CROATIA: 'HR', SERBIA: 'RS', UKRAINE: 'UA', TURKEY: 'TR', TURKIYE: 'TR',
    CYPRUS: 'CY', MALTA: 'MT', COLOMBIA: 'CO', PERU: 'PE', VENEZUELA: 'VE', ECUADOR: 'EC', URUGUAY: 'UY', 'COSTA RICA': 'CR', PANAMA: 'PA',
    JAMAICA: 'JM', 'DOMINICAN REPUBLIC': 'DO', BAHAMAS: 'BS', 'THE BAHAMAS': 'BS', BARBADOS: 'BB', 'TRINIDAD AND TOBAGO': 'TT', INDIA: 'IN',
    PAKISTAN: 'PK', BANGLADESH: 'BD', 'SRI LANKA': 'LK', NEPAL: 'NP', JAPAN: 'JP', 'SOUTH KOREA': 'KR', KOREA: 'KR', CHINA: 'CN',
    'HONG KONG': 'HK', TAIWAN: 'TW', SINGAPORE: 'SG', MALAYSIA: 'MY', PHILIPPINES: 'PH', THAILAND: 'TH', VIETNAM: 'VN',
    'UNITED ARAB EMIRATES': 'AE', UAE: 'AE', 'SAUDI ARABIA': 'SA', QATAR: 'QA', KUWAIT: 'KW', ISRAEL: 'IL', JORDAN: 'JO', LEBANON: 'LB',
    EGYPT: 'EG', NIGERIA: 'NG', GHANA: 'GH', KENYA: 'KE', 'SOUTH AFRICA': 'ZA', MOROCCO: 'MA', 'NEW ZEALAND': 'NZ',
    // multi-zone countries are recognised (so a US state code is not read out of their address) but have no single zone
    MEXICO: 'MX', BRAZIL: 'BR', ARGENTINA: 'AR', CHILE: 'CL', RUSSIA: 'RU', AUSTRALIA: 'AU', INDONESIA: 'ID', CANADA: 'CA',
    'UNITED STATES': 'US', 'UNITED STATES OF AMERICA': 'US', USA: 'US', US: 'US', 'U S A': 'US', 'U S': 'US', AMERICA: 'US',
});

const SUFFIX_COUNTRY = /(?:^|[\s,])(UNITED STATES OF AMERICA|UNITED STATES|USA|US|CANADA)\s*$/;
const CA_POSTAL = /\b[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d\b/i;
const US_ZIP = /\b\d{5}(?:-\d{4})?\b/;

// "St. Louis" -> "ST LOUIS", "U.S.A." -> "USA"
const clean = (text) => String(text || '').toUpperCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();

/** The text of one address entry / string / structured object, or ''. */
function addressText(address) {
    if (!address) return '';
    if (typeof address === 'string') return address;
    if (typeof address !== 'object') return '';
    if (typeof address.value === 'string') return address.value;
    return [address.line1 || address.street, address.city, address.state || address.region || address.province, address.postal_code || address.zip, address.country]
        .filter(Boolean).join(', ');
}

/** { state, country } hints from a structured address object ({ state, province, region, country }), else null. */
function structuredHints(address) {
    if (!address || typeof address !== 'object') return null;
    const state = address.state || address.region || address.province;
    const country = address.country || address.country_code;
    return state || country ? { state: state ? clean(state) : '', country: country ? clean(country) : '' } : null;
}

function countryCodeOf(name) {
    return COUNTRY_NAMES[clean(name)] || (clean(name).length === 2 ? clean(name) : null);
}

/** Look a trailing 1-3 word state / province up in the code and name tables. */
function trailingRegion(words, codes, names) {
    for (let size = Math.min(4, words.length); size >= 1; size -= 1) {
        const candidate = words.slice(-size).join(' ');
        if (size === 1 && codes[candidate]) return candidate;
        if (names[candidate]) return names[candidate];
    }
    return null;
}

/**
 * The IANA zone an address implies, or null. Accepts the address text ("12 Peachtree St, Atlanta, GA 30303"; Mapbox's "..., Georgia
 * 30303, United States" too), a contact_methods entry ({ value }) or a structured object ({ state, country }).
 */
export function timeZoneForAddress(address) {
    const hints = structuredHints(address);
    let country = hints?.country ? countryCodeOf(hints.country) : null;
    let regionWords = hints?.state ? hints.state.split(' ') : null;

    if (!regionWords) {
        let text = clean(addressText(address)).replace(/,/g, ' , ').replace(/\s+/g, ' ').trim();
        if (!text) return null;
        const suffix = text.match(SUFFIX_COUNTRY);
        if (suffix) {
            country = countryCodeOf(suffix[1].replace(/\s+/g, ' ').trim()) || country;
            text = text.slice(0, suffix.index).replace(/[\s,]+$/, '');
        } else {
            // a named country at the end: "..., Germany", "..., United Kingdom"
            const tail = text.split(',').pop().trim();
            const named = COUNTRY_NAMES[tail] && !['US', 'CA'].includes(COUNTRY_NAMES[tail]) ? COUNTRY_NAMES[tail] : null;
            if (named) {
                country = named;
                text = text.slice(0, text.lastIndexOf(',')).trim();
            }
        }
        const hasCaPostal = CA_POSTAL.test(text);
        if (hasCaPostal && !country) country = 'CA';
        text = text.replace(CA_POSTAL, ' ').replace(US_ZIP, ' ').replace(/[\s,]+$/, '').replace(/\s+/g, ' ').trim();
        regionWords = text.replace(/,/g, ' ').split(' ').filter(Boolean);
    }

    if (country && country !== 'US' && country !== 'CA') return COUNTRY_ZONES[country] || null;
    if (country === 'CA') {
        const province = trailingRegion(regionWords, CA_PROVINCE_ZONES, CA_PROVINCE_NAMES);
        return province ? CA_PROVINCE_ZONES[province] : null;
    }
    // US (stated or assumed). A bare "CA"/"DE"/"IN" is the state, never the country: countries are only read when spelled out above.
    const state = trailingRegion(regionWords, US_STATE_ZONES, US_STATE_NAMES);
    if (state) return US_STATE_ZONES[state];
    if (!country) {
        const province = trailingRegion(regionWords, CA_PROVINCE_ZONES, CA_PROVINCE_NAMES);
        if (province) return CA_PROVINCE_ZONES[province];
    }
    return null;
}

/** The IANA zone of one phone number (any format), or null. Delegates to scheduledSend's area-code / country-code tables. */
export function timeZoneForPhone(phone) {
    const value = typeof phone === 'string' ? phone : phone?.value || phone?.number || phone?.phone || phone?.phone_number || '';
    if (!String(value).trim()) return null;
    return recipientTimeZoneForPhone(phoneForSave(String(value))) || null;
}

const isPrimary = (entry) => Boolean(entry && typeof entry === 'object' && entry.is_primary);
/** Primary entries first, then the rest in their given order. */
const inPrimaryOrder = (entries) => {
    const list = (entries || []).filter(Boolean);
    return [...list.filter(isPrimary), ...list.filter((entry) => !isPrimary(entry))];
};

/**
 * Suggest a zone for a contact: the PRIMARY address first, else the phone numbers (primary first).
 * @param {{ addresses?: Array<string|object>, phones?: Array<string|object> }} source
 * @returns {{ timeZone: string, source: 'address'|'phone' } | null}
 */
export function suggestContactTimeZone({ addresses = [], phones = [] } = {}) {
    const primaryAddress = inPrimaryOrder(addresses)[0];
    const fromAddress = primaryAddress ? timeZoneForAddress(primaryAddress) : null;
    if (fromAddress) return { timeZone: fromAddress, source: 'address' };
    for (const phone of inPrimaryOrder(phones)) {
        const fromPhone = timeZoneForPhone(phone);
        if (fromPhone) return { timeZone: fromPhone, source: 'phone' };
    }
    return null;
}

/** The addresses and phones of a stored contact (contact_methods when loaded, else the flat mirrors on the row). */
export function contactAddressesAndPhones(contact) {
    const methodAddresses = entriesOf(contact, 'address').filter((entry) => (entry.value || '').trim());
    const flatAddresses = Array.isArray(contact?.addresses) ? contact.addresses.filter(Boolean) : [];
    return {
        addresses: methodAddresses.length ? methodAddresses : flatAddresses,
        phones: entriesOf(contact, 'phone').filter((entry) => (entry.value || '').trim()),
    };
}

/** { timeZone, source: 'stored'|'address'|'phone' } for a contact, or null when nothing is known. */
export function resolveContactTimeZoneDetail(contact) {
    if (contact?.time_zone) return { timeZone: contact.time_zone, source: 'stored' };
    return suggestContactTimeZone(contactAddressesAndPhones(contact));
}

/**
 * The IANA zone to use for a contact: the stored contact.time_zone, else a suggestion from the address / phone, else null.
 * @returns {string | null}
 */
export function resolveContactTimeZone(contact) {
    return resolveContactTimeZoneDetail(contact)?.timeZone || null;
}

/**
 * What the contact card's draft should hold after an edit: a person's choice (manual) is kept as is; otherwise the zone follows the
 * suggestion for the CURRENT address / phones (so an address change refreshes it) and is stored as auto.
 * @returns {{ time_zone: string|null, time_zone_source: 'auto'|'manual'|null }}
 */
export function timeZoneFieldsForSave({ time_zone: current = null, time_zone_source: source = null, addresses = [], phones = [] } = {}) {
    if (source === 'manual' && current) return { time_zone: current, time_zone_source: 'manual' };
    const suggestion = suggestContactTimeZone({ addresses, phones });
    return suggestion ? { time_zone: suggestion.timeZone, time_zone_source: 'auto' } : { time_zone: null, time_zone_source: null };
}

// ---------------------------------------------------------------------------------------------
// Showing it
// ---------------------------------------------------------------------------------------------

const ZONE_NAMES = Object.freeze({
    'America/New_York': 'Eastern', 'America/Chicago': 'Central', 'America/Denver': 'Mountain', 'America/Phoenix': 'Arizona',
    'America/Los_Angeles': 'Pacific', 'America/Anchorage': 'Alaska', 'Pacific/Honolulu': 'Hawaii', 'America/Puerto_Rico': 'Puerto Rico',
    'America/Toronto': 'Eastern (Canada)', 'America/Halifax': 'Atlantic (Canada)', 'America/St_Johns': 'Newfoundland',
    'America/Winnipeg': 'Central (Canada)', 'America/Regina': 'Saskatchewan', 'America/Edmonton': 'Mountain (Canada)',
    'America/Vancouver': 'Pacific (Canada)',
});

/** The zones offered in the Time zone select, US first. Any other stored zone is added by timeZoneOptions(). */
export const TIME_ZONE_CHOICES = Object.freeze([
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
    'America/Puerto_Rico', 'America/Toronto', 'America/Halifax', 'America/St_Johns', 'America/Winnipeg', 'America/Regina', 'America/Edmonton',
    'America/Vancouver', 'America/Mexico_City', 'America/Bogota', 'America/Sao_Paulo', 'Europe/London', 'Europe/Dublin', 'Europe/Paris',
    'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Athens', 'Europe/Istanbul', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore',
    'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Seoul', 'Australia/Sydney', 'Pacific/Auckland', 'Africa/Johannesburg',
]);

/** "Eastern (ET)", "Europe/London (GMT)": the plain-English name and short abbreviation of a zone at a moment. */
export function timeZoneLabel(timeZone, at = new Date()) {
    if (!timeZone) return '';
    const name = ZONE_NAMES[timeZone] || timeZone.replace(/_/g, ' ');
    const abbreviation = zoneAbbreviation(at, timeZone);
    return abbreviation && abbreviation !== timeZone ? `${name} (${abbreviation})` : name;
}

/** Options for the Time zone select: the usual zones, plus the stored one if it is not among them. */
export function timeZoneOptions(current = null, at = new Date()) {
    const zones = current && !TIME_ZONE_CHOICES.includes(current) ? [current, ...TIME_ZONE_CHOICES] : [...TIME_ZONE_CHOICES];
    return zones.map((value) => ({ value, label: timeZoneLabel(value, at) }));
}

/** The contact's current local time, "9:41 AM", or '' when the zone is unknown or invalid. */
export function contactLocalTime(timeZone, at = new Date()) {
    if (!timeZone) return '';
    try {
        return new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(at).replace(/[  ]/g, ' ');
    } catch {
        return '';
    }
}
